import { ProductDoc, ProductInventoryDoc, ProductInventoryUnit, ProductSupplierDoc } from '@gooduncles/gu-app-schema';
import { Dictionary } from '@reduxjs/toolkit';
import { Button, Empty, message } from 'antd';
import { FC, useState } from 'react';

import { updateProductInventorySupplierId } from 'src/utils/firebase/firebase-short-cut';

import CardItem from 'src/atomic-components/atoms/CardItem/CardItem';
import CardTitle from 'src/atomic-components/atoms/CardItem/CardTitle';
import Divider from 'src/atomic-components/atoms/Divider/Divider';
import ProductSupplierSelector from 'src/atomic-components/atoms/Seletor/ProductSupplierSelector';
import Label from 'src/atomic-components/atoms/TextItem/Label';
import Value from 'src/atomic-components/atoms/TextItem/Value';

import classes from './InventoryDetailPanel.module.scss';

import PurchaseConfigInfo from '../PurchaseConfigInfo/PurchaseConfigInfo';
import InventoryUnitCard from './InventoryUnitCard';

interface InventoryDetailPanelProps {
  inventory: ProductInventoryDoc | undefined;
  supplier: ProductSupplierDoc | undefined;
  productsMap: Dictionary<ProductDoc[]>;
  refreshInventory: () => Promise<ProductInventoryDoc | undefined>;
  refreshProducts: () => Promise<ProductDoc[] | undefined>;
}

const InventoryDetailPanel: FC<InventoryDetailPanelProps> = ({
  inventory,
  supplier,
  productsMap,
  refreshInventory,
  refreshProducts,
}) => {
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | undefined>(supplier?._id);
  const [isEditingSupplier, setIsEditingSupplier] = useState(false);

  const handleEditSupplier = () => {
    setIsEditingSupplier(true);
  };

  const handleEditSupplierSave = async () => {
    if (!inventory) return;
    if (!selectedSupplierId) return;
    try {
      await updateProductInventorySupplierId(inventory._id, selectedSupplierId);
      message.success('거래처를 변경했습니다.');
    } catch (error) {
      console.error(error);
      message.error('거래처 변경에 실패했습니다.');
    } finally {
      setIsEditingSupplier(false);
      refreshInventory();
    }
  };

  const handleEditSupplierCancel = () => {
    setIsEditingSupplier(false);
  };

  if (!inventory) {
    return <Empty description='재고 정보가 없습니다.' />;
  }

  return (
    <div className={classes.section}>
      <div className={classes.card}>
        <CardItem>
          <CardTitle title={inventory.inventoryName} />
        </CardItem>
        <CardItem>
          <Label>재고량</Label>
          <Value>{inventory.quantity}</Value>
        </CardItem>
        <Divider />
        <CardItem>
          <CardTitle title='거래처' />
          {!isEditingSupplier ? (
            <Button size='small' onClick={handleEditSupplier}>
              수정
            </Button>
          ) : (
            <div style={{ display: 'flex', gap: 4 }}>
              <Button type='primary' size='small' onClick={handleEditSupplierSave}>
                저장
              </Button>
              <Button size='small' onClick={handleEditSupplierCancel}>
                취소
              </Button>
            </div>
          )}
        </CardItem>
        <CardItem>
          {isEditingSupplier ? (
            <ProductSupplierSelector
              productSupplierId={supplier?._id}
              onProductSupplierChange={setSelectedSupplierId}
            />
          ) : (
            <Label>{supplier?.name}</Label>
          )}
        </CardItem>
        <Divider />
        <PurchaseConfigInfo
          inventoryId={inventory._id}
          uom={inventory.uom}
          purchaseConfig={inventory.purchaseConfig}
          onRefresh={refreshInventory}
        />
      </div>
      <div className={classes.card}>
        <InventoryUnitCard
          inventoryId={inventory._id}
          unit={ProductInventoryUnit.EA}
          uom={inventory.uom.ea}
          products={productsMap.ea}
          onRefresh={() => {
            refreshInventory();
            refreshProducts();
          }}
        />
      </div>
      <div className={classes.card}>
        {/* TODO: 삭제 기능 */}
        <InventoryUnitCard
          inventoryId={inventory._id}
          unit={ProductInventoryUnit.BUNDLE}
          uom={inventory.uom.bundle}
          products={productsMap.bundle}
          onRefresh={() => {
            refreshInventory();
            refreshProducts();
          }}
        />
      </div>
    </div>
  );
};

export default InventoryDetailPanel;
